import {
  NEXUS_BUG_LIMITS,
  type NexusBugInput,
  type NexusBugLabel,
  type NexusDeliveredTask,
  type NexusDeliveredTaskMatch,
  type NexusLedgerDelivery,
  type NexusLedgerOutcome,
} from '../../../../domain/nexus/model';

export const DELIVERED_TASKS_PAGE_SIZE = 100;
export const DELIVERED_TASKS_MAX_LIMIT = 500;

export const NEXUS_TABS = [
  { id: 'delivered-tasks', label: 'Delivered Tasks' },
  { id: 'deliveries', label: 'Deliveries' },
  { id: 'bugs', label: 'Bugs' },
] as const;

export type NexusTabId = (typeof NEXUS_TABS)[number]['id'];

/** `?tab=` value → tab. Delivered Tasks is the default for anything else. */
export function parseNexusTab(value: string | null): NexusTabId {
  return NEXUS_TABS.find((tab) => tab.id === value)?.id ?? 'delivered-tasks';
}

/** `Project Name - Project Owner`, or whichever one is set; empty when neither is. */
export function bugLabel(bug: Partial<NexusBugLabel> | null | undefined): string {
  return [bug?.projectName?.trim(), bug?.projectOwner?.trim()].filter(Boolean).join(' - ');
}

/** URL search for a bug's edit page on the Bugs tab. */
export function bugEditorSearch(bugId: string): string {
  return `?${new URLSearchParams({ tab: 'bugs', bug: bugId }).toString()}`;
}

/** Field errors for the bug form, matching the server limits. Empty when valid. */
export function bugInputErrors(input: NexusBugInput): Partial<Record<keyof NexusBugInput, string>> {
  const errors: Partial<Record<keyof NexusBugInput, string>> = {};
  for (const field of Object.keys(NEXUS_BUG_LIMITS) as (keyof NexusBugInput)[]) {
    const max = NEXUS_BUG_LIMITS[field];
    if (input[field].trim().length > max) errors[field] = `Keep this under ${max} characters.`;
  }
  return errors;
}

const OUTCOME_LABELS: Record<NexusLedgerOutcome, string> = {
  ingested: 'Delivered',
  submitted: 'Awaiting results',
  unknown: 'Outcome unknown',
  rejected: 'Not accepted',
};

export function outcomeLabel(outcome: NexusLedgerOutcome | null): string {
  return outcome ? OUTCOME_LABELS[outcome] : 'Unknown';
}

export function outcomeTone(outcome: NexusLedgerOutcome | null): string {
  if (outcome === 'ingested') return 'bg-emerald-100 text-emerald-700';
  if (outcome === 'rejected') return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
}

/** `TASK_STATE_READY_FOR_REVIEW` → `ready for review`. */
export function taskStateLabel(state: string | null): string | null {
  if (!state) return null;
  return state.replace(/^TASK_STATE_/, '').replaceAll('_', ' ').toLowerCase();
}

/** Long content-hash versions are shortened for the table; the full value goes in a tooltip. */
export function shortVersion(version: string | null): string | null {
  if (!version) return null;
  return version.length > 12 ? `${version.slice(0, 10)}…` : version;
}

export function sourceKindLabel(kind: string | null | undefined): string {
  if (kind === 'gcs') return 'GCS';
  if (kind === 'local') return 'Local folder';
  if (kind === 'coordinatorBatch') return 'Pipeline batch';
  return 'Unknown source';
}

/** Where the task came from: its own path when known, else the delivery's source. */
export function sourceLocation(task: Pick<NexusDeliveredTask, 'sourcePath' | 'sourceUri'>): string | null {
  return task.sourcePath || task.sourceUri || null;
}

/** Projects other than the current one, newest first as recorded. */
export function otherProjectIds(task: Pick<NexusDeliveredTask, 'projectIds' | 'currentProjectId'>): string[] {
  return (task.projectIds ?? []).filter((id) => id !== task.currentProjectId).reverse();
}

/** An attempt after the current version that has not resolved, such as a pending edit. */
export function pendingAttempt(task: Pick<NexusDeliveredTask, 'status' | 'lastAttempt' | 'currentDeliveryId'>) {
  const attempt = task.lastAttempt;
  if (!attempt || task.status !== 'ingested' || attempt.deliveryId === task.currentDeliveryId) return null;
  return attempt.outcome === 'submitted' || attempt.outcome === 'unknown' ? attempt : null;
}

/** The full Nexus identity, `id@version`, when Nexus returned one. */
export function copyableTaskId(task: Pick<NexusDeliveredTask, 'taskId' | 'versionedTaskId'>): string {
  return task.versionedTaskId || task.taskId;
}

const MATCH_LABELS: Record<NexusDeliveredTaskMatch, string> = {
  taskId: 'task ID',
  sourceTaskId: 'source ID',
  deliveryBugId: 'bug ID',
  projectId: 'Nexus project',
};

export function resultsCaption(
  result: { query: string; matchedOn?: NexusDeliveredTaskMatch[]; tasks: unknown[]; hasMore: boolean } | null,
): string {
  if (!result) return '';
  const count = result.tasks.length;
  if (!result.query) return result.hasMore ? `${count} most recent delivered tasks` : `${count} delivered task${count === 1 ? '' : 's'}`;
  const noun = count === 1 ? 'match' : 'matches';
  const matched = (result.matchedOn ?? []).map((kind) => MATCH_LABELS[kind]).join(', ');
  return `${count}${result.hasMore ? '+' : ''} ${noun} for “${result.query}”${matched ? ` · ${matched}` : ''}`;
}

const BUG_PARAM_RE = /^\d+$/;
const PROJECT_PARAM_RE = /^(P-[A-Za-z0-9_-]+|attempt-[A-Za-z0-9_-]+)$/;

/** `?bug=&project=` → the drill-down level. A project without a valid bug is ignored. */
export function parseDrillDown(params: URLSearchParams): { bug: string | null; project: string | null } {
  const bug = params.get('bug');
  const project = params.get('project');
  const validBug = bug && BUG_PARAM_RE.test(bug) ? bug : null;
  return { bug: validBug, project: validBug && project && PROJECT_PARAM_RE.test(project) ? project : null };
}

/** Search string for a drill-down level; an empty string is the overview. */
export function drillDownSearch({ bug, project }: { bug?: string | null; project?: string | null } = {}): string {
  const params = new URLSearchParams();
  if (bug) params.set('bug', bug);
  if (bug && project) params.set('project', project);
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Comma-separated, no spaces, for pasting into Nexus or a spreadsheet. */
export function commaList(ids: string[]): string {
  return ids.join(',');
}

export function deliveryLabel(delivery: Pick<NexusLedgerDelivery, 'remoteProjectId' | 'deliveryId'>): string {
  return delivery.remoteProjectId || 'Outcome unknown';
}

/** `DELIVERY_TYPE_EDITS` → `edits`; APPEND is the default and reads as `new tasks`. */
export function deliveryTypeLabel(type: string | null): string {
  if (!type || type === 'DELIVERY_TYPE_APPEND') return 'new tasks';
  return type.replace(/^DELIVERY_TYPE_/, '').replaceAll('_', ' ').toLowerCase();
}

/** Project IDs on a bug page, in display order, without unknown-outcome attempts. */
export function projectIdsOf(projects: Pick<NexusLedgerDelivery, 'remoteProjectId'>[]): string[] {
  return Array.from(new Set(projects.map((project) => project.remoteProjectId).filter((id): id is string => Boolean(id))));
}

/** Validator states for one task: `3 passed · 1 failed`. */
export function validationSummary(states: Record<string, number> | null): string | null {
  if (!states) return null;
  const parts = Object.entries(states).filter(([, count]) => count > 0)
    .map(([state, count]) => `${count} ${state.replace(/^STATE_/, '').toLowerCase()}`);
  return parts.length ? parts.join(' · ') : null;
}
